import React from "react";
import { BrowserRouter, Switch, Route, Redirect, withRouter } from "react-router-dom";
import { compose } from "recompose";
import { contains } from "ramda";

// redux (with redux-persist)
import { Provider } from "react-redux";
import { store, persistor } from "./reduxStore";
import { PersistGate } from "redux-persist/integration/react";

// apollo
import { ApolloProvider } from "react-apollo";
import setupApolloClient from "./apolloClient";

// material-ui
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { grey, amber, red } from "@material-ui/core/colors";

import withConnectionDetection from "./hocs/withConnectionDetection";
import ScrollToTop from "./hocs/ScrollToTop";

import Footer from "./layouts/Footer";
import Header from "./layouts/Header";

import CustomerBenefit from "./screens/CustomerBenefit";
import Topic from "./screens/Topic";
import Component from "./screens/Component";
import SellerInformation from "./screens/SellerInformation";
import TopicCluster from "./screens/TopicCluster";
import Login from "./screens/Login";
import Disclaimer from "./screens/Disclaimer";
import News from "./screens/News";
import EntryScreen from "./screens/EntryScreen";

import Spinner from "./components/Spinner";
import ConnectionStatusPopup from "./components/ConnectionStatusPopup";

import TCO from "./tco";
import TcoFrame from "./tco/tcoframe";
import Podcasts from "./screens/Podcasts";
import Podcast from "./screens/Podcast";
import EMobility from "./emobility/EMobility";
import EMobilityTopic from "./emobility/EMobilityTopic";
import EMobilityChapter from "./emobility/EMobilityChapter";
import Tracker from "./components/Tracker";
import TransportSolutions from "./transportsolutions/TransportSolutions";
import TransportSolutionsTopic from "./transportsolutions/TransportSolutionsTopic";
import TransportSolutionsChapter from "./transportsolutions/TransportSolutionsChapter";

const muiTheme = createMuiTheme({
    palette: {
        primary: grey,
        accent: amber,
        error: red,
        type: "light",
    },
});

const AppContainer = ({ location }) => {
    const hasAuthToken = !!localStorage.getItem("token");
    const isLoginScreen = location.pathname === "/login";

    const hideHeader = contains(location.pathname, [
        "/tco-lcc/defaultparameters",
        "/tco-lcc/type",
        "/tco-lcc/configuration",
    ]);
    const hideFooter = contains(location.pathname, [
        "/tco",
        "/tco-lcc",
        "/tco-lcc/",
        "/tco-lcc/defaultparameters",
        "/tco-lcc/type",
        "/tco-lcc/configuration",
        "/tco-lcc/result",
    ]);

    return hasAuthToken || isLoginScreen ? (
        <div
            className="app-container"
            style={{
                height: "100vh",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f0f1f2",
                fontFamily: "MAN-light, Arial, sans-serif",
            }}
        >
            {hideHeader ? null : <Header isLoggedIn={hasAuthToken} />}
            <ConnectionStatusPopup />
            <Switch>
                <Route exact path="/(|home)" component={EntryScreen} />
                <Route exact path="/login" component={Login} />
                <Route exact path="/how-to" component={Component} />
                <Route exact path="/disclaimer" component={Disclaimer} />
                <Route path="/news/:page?" component={News} />
                <Route path="/vehicle/:vehicleSlug/customer-benefit/:slug" component={CustomerBenefit} />
                <Route path="/vehicle/:vehicleSlug/topic/:slug" component={Topic} />
                <Route path="/vehicle/:vehicleSlug/component/:slug" component={Component} />
                <Route path="/vehicle/:vehicleSlug" component={SellerInformation} />

                <Route path="/topic-cluster/:topicClusterSlug/:slug" component={Topic} />
                <Route path="/topic-cluster/:topicClusterSlug" component={TopicCluster} />
                <Route path="/tco-lcc" component={TCO} />
                <Route path="/tco" component={TcoFrame} />
                <Route path="/buscast" exact component={Podcasts} />
                <Route path="/buscast/:id" component={Podcast} />
                <Route path="/e-mobility" exact component={EMobility} />
                <Route path="/e-mobility/:topicSlug" exact component={EMobilityTopic} />
                <Route path="/e-mobility/:topicSlug/:slug" exact component={EMobilityChapter} />
                <Route path="/transport-solutions" exact component={TransportSolutions} />
                <Route path="/transport-solutions/:topicSlug" exact component={TransportSolutionsTopic} />
                <Route path="/transport-solutions/:topicSlug/:slug" exact component={TransportSolutionsChapter} />

                <Redirect from="*" to="/" />
            </Switch>
            <Tracker />
            {hideFooter ? null : <Footer isLoggedIn={hasAuthToken} />}
        </div>
    ) : (
        <Redirect to={{ pathname: "/login", state: { from: location } }} />
    );
};

const EnhancedAppContainer = compose(withRouter, withConnectionDetection)(AppContainer);

class App extends React.Component {
    state = {
        apolloClient: null,
        clientReady: false,
    };

    async componentDidMount() {
        setupApolloClient()
            .then((client) => {
                this.setState({ apolloClient: client, clientReady: true });
            })
            .catch((error) => {
                console.log("error apollo client setup", error);
            });
    }

    render() {
        if (!this.state.clientReady) {
            return <Spinner />;
        }

        return (
            <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <ApolloProvider client={this.state.apolloClient}>
                        <BrowserRouter>
                            <ScrollToTop>
                                <MuiThemeProvider theme={muiTheme}>
                                    <EnhancedAppContainer />
                                </MuiThemeProvider>
                            </ScrollToTop>
                        </BrowserRouter>
                    </ApolloProvider>
                </PersistGate>
            </Provider>
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js